import { addAudioBreadcrumb } from "../../audio/audioSentry";
import type { createSongsApi } from "../../data/songs/api";
import type { DataSession } from "../../../../data/core/session";
import { isOfflineAudioUrl } from "../../audio/resolveSource";
import {
  isOnDemandMasterUrl,
  pollForPlayableAudio,
} from "./pollForPlayableAudio";
import type { Song } from "../../domain/types";

export type EnsureSongPlayableResult =
  | { playable: true; song: Song }
  | { playable: false };

/**
 * THE gate every internal song goes through immediately before playback
 * actually starts — see `playback.slice.ts`'s `play`/`playWithStartTime`,
 * which call this for every play regardless of how the song was reached
 * (tap-to-play, queue navigation, deep link, etc.). There is no
 * "unpublished" status/branch anywhere in the app: every internal song's
 * `urlOnline` points at a deterministic CDN path that may or may not be
 * materialized yet (new catalog content transcodes on-demand — the same
 * mechanism that used to be gated behind a status check is now just how
 * all internal-song audio becomes available), so every play polls it
 * first, uniformly.
 *
 * Fast path (the overwhelming majority of plays — anything already
 * materialized): the first poll tick returns 200 immediately, so this
 * resolves after a single network round-trip and returns `song` unchanged
 * — indistinguishable from a direct play.
 *
 * Slow path (brand-new content, a lost publish intent, or a mid-poll
 * tmp/final master crossover): the poll keeps retrying for up to ~10s
 * (firing a one-shot self-rescue kick — see `pollForPlayableAudio`), and
 * once it succeeds, re-fetches the song bypassing cache before returning it
 * — a poll success only confirms the CDN object exists, not that this
 * specific in-memory `song` reflects the latest row (e.g. duration/credits
 * finalized alongside the audio). Skipped entirely on the fast path to
 * avoid an extra round trip on every single play.
 *
 * Two-master publish: `pollForPlayableAudio` returns the URL that actually
 * resolved playable (`playableUrl`), which may differ from `song.urlOnline`
 * — e.g. the poll started on the tmp master and crossed over to the final
 * one (or vice versa) mid-poll. Whenever it differs, the returned song's
 * `urlOnline` is overridden with `playableUrl` so downstream `prepareTrack`
 * / `resolveSource` — and ultimately the player — resolve the URL that was
 * actually confirmed playable, not the (possibly stale) one the caller
 * started with.
 *
 * Returns `{playable: false}` on timeout or missing `urlOnline` — the
 * caller is responsible for the "Coming soon"-style fallback UI.
 *
 * Local/offline exemption: by the time `play()` calls this,
 * `resolveQueueItemForPlayback` has already substituted `urlOnline` with a
 * local `file://` path for downloaded songs (see
 * `data/localAudioPaths.ts`'s `substituteLocalSongAssets`). A local file
 * isn't a CDN HLS master — polling it with `pollForPlayableAudio` (HTTP
 * fetch + playlist-body checks) would just hang for the full timeout and
 * fall back to the "Coming soon" toast, breaking offline playback. Uses
 * the same `isOfflineAudioUrl` check `resolveSource`/`prepareTrack` use to
 * make this exact distinction, so it can never disagree with what actually
 * plays the track.
 */
export async function ensureSongPlayable(
  song: Song,
  fetchFresh: ReturnType<typeof createSongsApi>["fetchFreshSongById"],
  data: DataSession,
): Promise<EnsureSongPlayableResult> {
  const songId = song.id;

  if (isOfflineAudioUrl(song.urlOnline)) {
    return { playable: true, song };
  }

  if (!song.urlOnline) {
    // Should never happen for an internal song under the current contract
    // (urlOnline is always populated) — pure defensive fallback.
    addAudioBreadcrumb(
      "ondemand_publish.missing_url_online",
      { songId },
      "warning",
    );
    return { playable: false };
  }

  // Anonymous catalogue responses carry a public preview media playlist,
  // not an on-demand tmp/final master. Play that exact URL directly: adding
  // `.tmp` would manufacture `preview.tmp.m3u8`, which does not exist.
  if (!isOnDemandMasterUrl(song.urlOnline)) {
    return { playable: true, song };
  }

  const pollResult = await pollForPlayableAudio(data, songId, song.urlOnline);

  if (!pollResult.ready) {
    addAudioBreadcrumb("ondemand_publish.timeout", { songId }, "warning");
    return { playable: false };
  }

  const { playableUrl, usedRescue } = pollResult;

  // Land the resolved playable URL onto whichever song object we end up
  // returning — a no-op (same reference) when the poll resolved on the
  // exact URL the caller started with, an override when it crossed from
  // tmp to final (or vice versa) mid-poll.
  const withPlayableUrl = (candidate: Song): Song =>
    candidate.urlOnline === playableUrl
      ? candidate
      : { ...candidate, urlOnline: playableUrl };

  if (!usedRescue) {
    // Fast path: materialized on the very first tick — the metadata we
    // already have is current, skip the extra network round trip.
    return { playable: true, song: withPlayableUrl(song) };
  }

  // Slow path: had to wait for materialization (and fired a rescue) —
  // re-fetch bypassing cache to pick up any metadata finalized alongside
  // the audio, not just re-confirm playability.
  const freshSong = await fetchFresh(songId);
  if (!freshSong) {
    addAudioBreadcrumb(
      "ondemand_publish.refetch_failed",
      { songId },
      "warning",
    );
    // The CDN object IS confirmed playable — fall back to the metadata we
    // already have rather than failing playback entirely over a refetch
    // hiccup.
    return { playable: true, song: withPlayableUrl(song) };
  }

  addAudioBreadcrumb("ondemand_publish.success", { songId }, "info");
  return { playable: true, song: withPlayableUrl(freshSong) };
}
